
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Close,
    OpenInNew,
    EmojiEvents,
    LinkedIn,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

const CertificatesModal = ({ open, onClose }) => {
    const theme = useTheme();
    const { darkMode } = useThemeMode();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const certificateCategories = [
        {
            platform: 'HackerRank',
            certificates: 10,
            badges: 5,
            url: 'https://www.hackerrank.com/profile/iyogesh_more',
            color: darkMode ? '#00d4aa' : '#0a3b1f',
        },
        {
            platform: 'LeetCode',
            count: 19,
            type: 'Badges',
            url: 'https://leetcode.com/u/iamyogeshmore/',
            color: darkMode ? '#ffa726' : '#f57c00',
        },
        {
            platform: 'Learntube',
            count: 2,
            type: 'Certificates',
            url: 'https://www.linkedin.com/in/yogesh-more-28b663266/details/certifications/',
            color: darkMode ? '#42a5f5' : '#1976d2',
        },
        {
            platform: 'Simplilearn',
            count: 1,
            type: 'Certificates',
            url: 'https://www.linkedin.com/in/yogesh-more-28b663266/details/certifications/',
            color: darkMode ? '#ff7043' : '#d84315',
        },
        {
            platform: 'freeCodeCamp',
            count: 1,
            type: 'Certificates',
            url: 'https://www.freecodecamp.org/certification/IamYogeshMore/javascript-algorithms-and-data-structures-v8',
            color: darkMode ? '#66bb6a' : '#2e7d32',
        },
        {
            platform: 'Great Learning',
            count: 1,
            type: 'Certificates',
            url: 'https://www.linkedin.com/in/yogesh-more-28b663266/details/certifications/',
            color: darkMode ? '#ab47bc' : '#7b1fa2',
        },
        {
            platform: 'CipherByte',
            count: 1,
            type: 'Certificates',
            url: 'https://drive.google.com/file/d/1-muzfNhk8EzUgxcZgykf5kHoRhCYOWIW/view',
            color: darkMode ? '#ec407a' : '#c2185b',
        },
        {
            platform: 'Job Ready MERN Full-Stack Web Development Course',
            count: 1,
            type: 'Certificates',
            url: 'https://courses.30dayscoding.com/verify-certificate?serialno=OD95MFGP',
            color: darkMode ? '#ffb74d' : '#f57c00',
        },
        {
            platform: 'BridgeLabz Fellowship Program',
            count: 1,
            type: 'Certificates',
            url: 'https://drive.google.com/file/d/1bwKYsl_btzU_WMjXO4tpvkEDpCr2aRfz/view',
            color: darkMode ? '#81c784' : '#388e3c',
        },
    ];

    // Calculate total certificates and badges
    const totalCertificates = certificateCategories.reduce((sum, cat) => {
        if (cat.platform === 'HackerRank') {
            return sum + cat.certificates;
        }
        return cat.type === 'Certificates' ? sum + cat.count : sum;
    }, 0);

    const totalBadges = certificateCategories.reduce((sum, cat) => {
        if (cat.platform === 'HackerRank') {
            return sum + cat.badges;
        }
        return cat.type === 'Badges' ? sum + cat.count : sum;
    }, 0);



    const handleViewCertificate = (url) => {
        window.open(url, '_blank');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: isMobile ? 0 : 3,
                    background: darkMode
                        ? 'linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(42,42,42,0.95) 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                    backdropFilter: 'blur(20px)',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 2,
                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmojiEvents sx={{ fontSize: 28, color: 'primary.main' }} />
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                fontSize: { xs: '1.25rem', md: '1.5rem' },
                                mb: 0.5,
                            }}
                        >
                            Certificates & Achievements
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.85rem',
                                    fontFamily: '"JetBrains Mono", monospace',
                                }}
                            >
                                {totalCertificates} Certificates • {totalBadges} Badges
                            </Typography>
                            <Box
                                component="a"
                                href="https://www.linkedin.com/in/yogesh-more-28b663266/details/certifications/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 2,
                                    backgroundColor: '#0077b5',
                                    color: 'white',
                                    textDecoration: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#005885',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 2px 8px rgba(0, 119, 181, 0.3)',
                                    },
                                }}
                            >
                                <LinkedIn sx={{ fontSize: 14 }} />
                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                                    Show all certifications
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="large">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {/* Certificates Section */}
                <Box>


                    <List sx={{ py: 0 }}>
                        {certificateCategories.map((cert, index) => (
                            <ListItem
                                key={index}
                                onClick={() => handleViewCertificate(cert.url)}
                                sx={{
                                    px: 0,
                                    py: 1.5,
                                    borderBottom: index < certificateCategories.length - 1 ? `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` : 'none',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        borderRadius: 2,
                                        transform: 'translateX(4px)',
                                        '& .MuiTypography-root': {
                                            color: cert.color + ' !important',
                                        },
                                        '& .platform-name': {
                                            color: cert.color + ' !important',
                                        },
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {/* Platform Color Indicator */}
                                <Box
                                    sx={{
                                        width: 4,
                                        height: 40,
                                        backgroundColor: cert.color,
                                        borderRadius: 2,
                                        mr: 2,
                                        flexShrink: 0,
                                    }}
                                />

                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                            <Typography
                                                variant="body1"
                                                className="platform-name"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    fontSize: '1rem',
                                                    cursor: 'pointer',
                                                    transition: 'color 0.2s ease',
                                                }}
                                            >
                                                {cert.platform}
                                            </Typography>
                                            {cert.platform === 'HackerRank' ? (
                                                // Special handling for HackerRank with both certificates and badges
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 2,
                                                            py: 0.5,
                                                            borderRadius: 3,
                                                            backgroundColor: `${cert.color}15`,
                                                            border: `1px solid ${cert.color}30`,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: cert.color,
                                                                fontSize: '0.85rem',
                                                                fontFamily: '"JetBrains Mono", monospace',
                                                            }}
                                                        >
                                                            {cert.certificates}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: cert.color,
                                                                fontSize: '0.8rem',
                                                            }}
                                                        >
                                                            Certificates
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 2,
                                                            py: 0.5,
                                                            borderRadius: 3,
                                                            backgroundColor: `${cert.color}15`,
                                                            border: `1px solid ${cert.color}30`,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: cert.color,
                                                                fontSize: '0.85rem',
                                                                fontFamily: '"JetBrains Mono", monospace',
                                                            }}
                                                        >
                                                            {cert.badges}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: cert.color,
                                                                fontSize: '0.8rem',
                                                            }}
                                                        >
                                                            Badges
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                // Regular handling for other platforms
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: 3,
                                                        backgroundColor: `${cert.color}15`,
                                                        border: `1px solid ${cert.color}30`,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: cert.color,
                                                            fontSize: '0.85rem',
                                                            fontFamily: '"JetBrains Mono", monospace',
                                                        }}
                                                    >
                                                        {cert.count}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: cert.color,
                                                            fontSize: '0.8rem',
                                                        }}
                                                    >
                                                        {cert.type}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    sx={{
                                        '& .MuiListItemText-primary': {
                                            mb: 0,
                                        },
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: cert.color,
                                        ml: 1,
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <OpenInNew fontSize="small" />
                                </Box>
                            </ListItem>
                        ))}
                    </List>


                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button onClick={onClose} variant="outlined" size="large">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CertificatesModal;